import { Link, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/common/PhoneInput";
import { DEFAULT_COUNTRY, parsePhone, toE164, type Country } from "@/lib/countries";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const schema = z.object({
  first_name: z.string().min(2),
  last_name: z.string().optional(),
  phone: z.object({
    country: z.object({ code: z.string(), name: z.string(), dial: z.string(), flag: z.string() }),
    local: z.string().min(6).max(15),
  }),
  email: z.string().email().optional().or(z.literal("")),
});
type FormValues = z.infer<typeof schema>;

export function ProfileEdit() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const initialPhone = parsePhone(profile?.phone ?? null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: profile?.first_name ?? "",
      last_name: profile?.last_name ?? "",
      phone: { country: initialPhone.country ?? DEFAULT_COUNTRY, local: initialPhone.local },
      email: profile?.email ?? "",
    },
  });

  async function onSubmit(data: FormValues) {
    if (!profile) return;
    try {
      const e164 = toE164(data.phone.country as Country, data.phone.local);

      const { error } = await supabase
        .from("users")
        .update({
          first_name: data.first_name.trim(),
          last_name: data.last_name?.trim() || null,
          phone: e164,
          email: data.email?.trim() || null,
        })
        .eq("id", profile.id);
      if (error) throw error;
      await qc.invalidateQueries({ queryKey: ["profile"] });
      toast.success(t("common.save"));
      navigate(-1);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-3">
        <Button asChild variant="ghost" size="icon"><Link to="/profile"><ArrowLeft /></Link></Button>
        <h1 className="font-display text-xl font-bold">{t("profile.edit")}</h1>
      </header>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">{t("auth.firstName")} *</Label>
          <Input id="first_name" {...form.register("first_name")} />
          {form.formState.errors.first_name && <p className="text-sm text-destructive">{form.formState.errors.first_name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="last_name">Nom</Label>
          <Input id="last_name" {...form.register("last_name")} />
        </div>

        <div className="space-y-2">
          <Label>{t("wallet.phone")} *</Label>
          <Controller
            control={form.control}
            name="phone"
            render={({ field }) => (
              <PhoneInput
                value={field.value as { country: Country; local: string }}
                onChange={field.onChange}
              />
            )}
          />
          {form.formState.errors.phone?.local && (
            <p className="text-sm text-destructive">{form.formState.errors.phone.local.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" disabled={!!profile?.email} {...form.register("email")} />
          <p className="text-xs text-muted-foreground">L'email est lié à ton compte Google et ne peut être changé ici.</p>
          {form.formState.errors.email && <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>}
        </div>

        <Button type="submit" size="lg" className="w-full" loading={form.formState.isSubmitting}>
          {t("common.save")}
        </Button>
      </form>
    </div>
  );
}
